import type { IconDefinition } from '../../shared/iconDefinition';
import { TwemojiGlyphImage } from '../twemojiGlyphImage';

// The screen before the video starts. Two jobs.
//
// One: teach nine icons in a few seconds, so a reviewer can hit the one they mean
// within about two seconds of feeling something without losing the video.
//
// Two: the anonymity notice. It costs nothing and it is the difference between
// honest data and a politeness machine (DESIGN-DECISIONS §6). This audience —
// friends, colleagues, family — will suppress negative signal by default, and
// negative signal is the whole reason the tool is worth using. The notice is not
// reassurance copy; it is the mechanism.
export function ViewerLegendScreen(input: {
  active: boolean;
  title: string | null;
  icons: IconDefinition[];
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  onStart: () => void;
  startDisabled: boolean;
  startLabel: string;
}) {
  return (
    <section className={input.active ? 'screen screenOn screenScroll' : 'screen'}>
      <div className="pageWrap">
        <h1>
          what<span>landed</span>
        </h1>
        <p className="pageSub">
          {input.title ? `“${input.title}” — ` : ''}
          Tap an icon whenever you feel something. That's the whole job.
        </p>

        <div className="legendGrid">
          {input.icons.map((icon, index) => (
            <div className="legendItem" key={icon.id}>
              <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={26} />
              <b>
                {index + 1}. {icon.label}
              </b>
            </div>
          ))}
        </div>

        {/* Cowork004 §3 — prominent, not fine print.
            The instruction to tap repeatedly is not permission, it is
            STANDARDISATION. Left unsaid, some reviewers tap once out of politeness
            and others mash, so tap counts measure who is demonstrative rather than
            what landed. Said out loud, intensity becomes comparable between people
            and the chart's tap counts mean something.
            The last line is an incentive at zero cost that also improves the data,
            because retention curves are only usable if people finish. It is framed
            as finishing the VIDEO, never as clicking the button — the replay gate
            is on Finish, and copy that reads as a shortcut would invite one. */}
        <div className="calloutLead">
          <p>
            <b>Tap what you feel, when you feel it.</b>
          </p>
          <p>If something really hits you, tap it as many times as you feel it.</p>
          <p>
            <b>Nobody sees what you tapped.</b> Your friend only sees the overall shape.
          </p>
          <p>Finish the video and you'll see how everyone else reacted.</p>
        </div>

        <div className="callout">
          <b>Turn your phone sideways</b> and let it fill the screen. On a laptop you can
          also use number keys <b>1–9</b> instead of clicking.
        </div>

        <input
          type="text"
          placeholder="Your name (optional — just so you recognise your own summary)"
          autoComplete="off"
          value={input.displayName}
          onChange={(event) => input.onDisplayNameChange(event.target.value)}
        />

        <button className="buttonPrimary" type="button" onClick={input.onStart} disabled={input.startDisabled}>
          {input.startLabel}
        </button>
      </div>
    </section>
  );
}
